import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import isEqual from 'lodash.isequal';
import type FinancingTermsOverview from '../../../contracts/FinancingTermsOverview';
import type { RootState } from '../../../store/types';
import {
  Tab,
  Title,
  Divider,
  SectionWrapper,
  FaqLink,
  FaqContainer
} from './ShowFinancingTerms.styles';
import LoadingPage from '../../../shared/components/LoadingPage';
import ErrorPage from '../../../shared/components/ErrorPage';
import TermsOverview from './TermsOverview';
import { financingTermsOperations, financingTermsSelectors } from '../ducks';
import ManageCoSignerButton from './ManageCoSignerButton/ManageCoSignerButton';
import CoSignerTerms from './CoSignerTerms/CoSignerTerms';

type StateProps = {
  financingTermsOverview: FinancingTermsOverview | null;
  isLoadingTermsOverview: boolean;
  termsOverviewError?: boolean | Record<string, unknown>;
};

type DispatchProps = {
  fetchTermsOverview: () => void;
};

function ShowFinancingTerms({
  fetchTermsOverview,
  isLoadingTermsOverview,
  termsOverviewError,
  financingTermsOverview
}: StateProps & DispatchProps) {
  useEffect(() => {
    fetchTermsOverview();
  }, []);

  const hasError = !isEqual(termsOverviewError, {});
  const shouldShowError = !isLoadingTermsOverview && hasError;
  const { cosignerAllowed, hasCoApplicant } = financingTermsOverview ?? {};
  const titleText = hasCoApplicant
    ? 'See terms with co-signer'
    : 'see better terms with co-signer added';

  const classForWrapper =
    hasCoApplicant && cosignerAllowed ? 'toggleCoApplicant' : 'addCoApplicant';

  return (
    <>
      {shouldShowError ? (
        <ErrorPage />
      ) : (
        <Tab>
          <SectionWrapper className={`financingTab ' + ${classForWrapper}`}>
            {cosignerAllowed && <Title className="coSignerTitle">{titleText}</Title>}
            {hasCoApplicant && cosignerAllowed && <CoSignerTerms />}
            {cosignerAllowed && <ManageCoSignerButton />}
          </SectionWrapper>
          <>
            {isLoadingTermsOverview ? <LoadingPage /> : <TermsOverview />}
            <Divider />
            <FaqContainer>
              <p>Have questions about your financing terms?</p>
              <FaqLink href="/help/payment-and-financing">View Frequently Asked Questions</FaqLink>
            </FaqContainer>
          </>
        </Tab>
      )}
    </>
  );
}

const mapStateToProps = (state: RootState) => ({
  isLoadingTermsOverview: financingTermsSelectors.getIsLoadingTermsOverview(state),
  termsOverviewError: financingTermsSelectors.getTermsOverviewError(state),
  financingTermsOverview: financingTermsSelectors.getFinancingTermsOverview(state)
});

const mapDispatchToProps = {
  fetchTermsOverview: financingTermsOperations.fetchTermsOverview
};

export default connect(mapStateToProps, mapDispatchToProps)(ShowFinancingTerms);
